<template>
  <v-card v-if="showView">
    <!-- Loading bar -->
    <v-progress-linear
      v-if="isLoading"
      indeterminate
      absolute
      color="primary"
    ></v-progress-linear>
    <v-container fluid>
      <v-row class="pt-2" align="center">
        <!-- Text input filter -->
        <v-col
          ><v-text-field
            ref="freehandFilter"
            v-model="search"
            variant="underlined"
            prepend-icon="mdi-magnify"
            label="Serial number, Order number, Product or Tenant"
            density="compact"
            hide-details
            clearable
            :rules="searchRules"
            @keyup.enter="onSearch"
            @click:clear="onClearSearchField"
          ></v-text-field
        ></v-col>
        <!-- Export to CSV button with hover text-->
        <v-col v-if="canExportCSV" cols="auto" class="d-flex justify-end"
          ><v-tooltip location="bottom">
            <template v-slot:activator="{ props }"
              ><v-btn
                icon
                variant="text"
                size="small"
                color="primary"
                :disabled="isLoading"
                v-bind="props"
                @click="exportToCsv"
                ><v-icon>mdi-export</v-icon>
              </v-btn></template
            >
            <span>Export to CSV</span>
          </v-tooltip></v-col
        >
      </v-row>
      <!-- Dropdown filters -->
      <v-row>
        <!-- Products select -->
        <v-col cols="2">
          <TreeSelect
            :label="$t('machines.dataTable.headers.product')"
            :items="availableProducts"
            :value="treeSelectedItems"
            @update:treeSelectedItems="onProductsSelection"
        /></v-col>
        <!-- Countries select -->
        <v-col cols="2"><CountryFilterMenu
            v-model="selectedCountries"
            @update:model-value="onCountrySelect"
          /></v-col>
        <!-- Segments select -->
        <v-col cols="2">
          <TreeSelect
            label="Segments"
            :items="availableSegments"
            :value="treeSelectedItems"
            @update:treeSelectedItems="onSegmentsSelection"
        /></v-col>
        <!-- Date select -->
        <v-col cols="2">
          <v-select
            :items="timeRangeItems"
            item-value="value"
            item-title="title"
            v-model="selectedTimeRange"
            label="Delivery date"
            variant="underlined"
            menu-icon="mdi-chevron-down"
            @update:model-value="onTimeRange"
            @click:clear="onClearTimeRange"
            clearable
            :menu-props="{ scrollStrategy: 'none' }"
          ></v-select>
        </v-col>
        <v-col cols="2">
          <!-- Custom date -->
          <v-menu
            ref="datePickerMenu"
            v-model="datePickerMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            location="bottom"
            scroll-strategy="none"
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ props }">
              <v-text-field
                v-model="dateRangeText"
                readonly
                :placeholder="$t('common.placeholders.selectDates')"
                :disabled="selectedTimeRange !== 'custom'"
                variant="underlined"
                v-bind="props"
              ></v-text-field>
            </template>
            <v-date-picker v-model="dates" multiple="range">
              <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" color="primary" @click="onClearTimeRange"
                  >Cancel</v-btn
                >
                <v-btn
                  variant="text"
                  color="primary"
                  :disabled="dates.length < 2"
                  @click="datePickerMenu = false"
                  >OK</v-btn
                >
              </template>
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <!-- Machines table -->
      <v-row
        ><v-col
          ><v-data-table
            :headers="headers"
            :items="dataTableItems"
            :items-per-page="50"
            :items-per-page-options="[5, 10, 20, 50]"
            :sort-by="[{ key: 'serialNo', order: 'asc' }]"
            class="no-pointer-change"
          >
            <template v-slot:[`item.image`]="{ item }">
              <v-avatar class="ma-1">
                <v-img
                  :src="machineImageSrc(item)"
                  onerror="this.onerror=null;this.src='/products/jpg/default.jpg';this.title='Missing product image'"
                  :alt="item.displayedProductName"
                />
              </v-avatar>
            </template>

            <template v-slot:[`item.latestEngineHours`]="{ item }">
              {{ formatEngineHours(item.latestEngineHours) }}
            </template>

            <template v-slot:[`item.deliveryDate`]="{ item }">
              {{ formatDate(item.deliveryDate) }}
            </template>
          </v-data-table></v-col
        ></v-row
      >
    </v-container>
    <v-snackbar
      v-model="snackbar"
      :timeout="2000"
      location="top"
      theme="dark"
      color="green-darken-1"
    >
      {{ snackbarText }}

      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">
          {{ $t("common.actions.close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>
<script>
import { mapActions } from "vuex"
import TreeSelect from "@/components/filters/TreeSelect"
import Formatter from "@/utils/formatter"
import Exporter from "@/utils/csvexport"
import CountryFilterMenu from "@/components/filters/CountryFilterMenu"

export default {
  props: ["tenantUid"],
  components: {
    CountryFilterMenu,
    TreeSelect
  },
  created() {
    this.$store.dispatch("loadSegmentMetadata", "en")
  },
  data() {
    return {
      isLoading: false,
      allFilters: {
        FilterTenantUid: "",
        SearchString: "",
        ProductIds: "",
        Countries: "",
        Start: "",
        End: "",
        Segments: []
      },
      // Freehand filter
      search: "",
      searchRules: [
        v =>
          /^[A-Za-z0-9 +]*$/.test(v) ||
          "Only letters, numbers, and + are allowed"
      ],
      // Segments filter
      treeSelectedItems: [],
      // Products filter
      products: [],
      // Countries filter
      selectedCountries: [],
      // Export snackbar
      snackbar: false,
      snackbarText: "",
      // Date pickers
      dates: [],
      datePickerMenu: false,
      selectedTimeRange: null,
      dateRangeText: "",
      timeRangeItems: [
        { title: this.$t("common.enums.day.today"), value: "0" },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            7
          )}`,
          value: "7"
        },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            30
          )}`,
          value: "30"
        },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            90
          )}`,
          value: "90"
        },
        { title: this.$t("common.filters.customRange"), value: "custom" }
      ],
      // Table
      headers: [
        {
          sortable: false,
          title: "",
          key: "image",
          width: 20
        },
        {
          title: "Product",
          key: "productName",
          align: "start"
        },
        {
          title: "Serial number",
          key: "serialNo",
          align: "start"
        },
        {
          title: "Order number",
          key: "orderNo",
          align: "start"
        },
        {
          title: "Delivery date",
          key: "deliveryDate",
          align: "start"
        },
        {
          title: "Engine hours",
          key: "latestEngineHours",
          align: "start"
        },
        {
          title: "Tenant",
          key: "tenantName",
          align: "start"
        },
        {
          title: "Country",
          key: "country",
          align: "start"
        },
        {
          title: "Segment",
          key: "segmentName",
          align: "start"
        },
        {
          title: "Category",
          key: "categoryName",
          align: "start"
        },
        {
          title: "Application",
          key: "applications",
          align: "start",
          sortable: false
        }
      ]
    }
  },
  computed: {
    showView() {
      return this.$store.getters.canReadFleetAnalytics
    },
    canExportCSV() {
      return this.$store.getters.isAdmin
    },
    dataTableItems() {
      let storeData = this.$store.getters.fleetAnalyticsMachines
      if (storeData != [] && storeData?.length > 0) {
        return storeData
      }
      return []
    },
    // Combine ID assignment and normalization to fill the TreeSelect
    availableSegments() {
      let adjustedItems = this.addIdsAndNormalizeSegments(
        this.$store.getters.segmentMetadata
      )
      return adjustedItems
    },
    availableProducts() {
      let adjustedItems = this.addIdsAndNormalizeProducts(
        this.$store.getters.grouppedProducts
      )
      return adjustedItems
    },
    selectedTenant() {
      return this.$store.getters.selectedTenant.uid
    }
  },
  methods: {
    ...mapActions(["setError"]),
    async fetchData() {
      try {
        this.isLoading = true
        await this.$store.dispatch("fetchFleetAnalyticsMachines", {
          tenantUid: this.tenantUid,
          filters: this.allFilters
        })
      } catch (err) {
        this.setError(err)
        this.isLoading = false
      }
      this.isLoading = false
    },
    onClearSearchField() {
      this.allFilters.SearchString = ""
      this.onApplyFilters()
    },
    onClearTimeRange() {
      this.datePickerMenu = false
      this.allFilters.Start = ""
      this.allFilters.End = ""
      this.dates = []
      this.selectedTimeRange = null
      this.onApplyFilters()
    },
    onCountrySelect(selection) {
      this.allFilters.Countries = selection.join(",")
    },
    onSegmentsSelection(selection) {
      this.allFilters.Segments = this.convertSelectedSegmentsfunction(
        selection,
        this.availableSegments
      )
    },
    onProductsSelection(selection) {
      const selectionAsArray = Array.from(selection)
      const selectionAsString = selectionAsArray.join(", ")
      this.allFilters.ProductIds = selectionAsString
    },
    // Recursive function: assign hierarchical IDs and normalize to {id, name, children}
    addIdsAndNormalizeSegments(items, path = "") {
      return items.map((item, index) => {
        const id = path ? `${path}.${index + 1}` : `${index + 1}`

        // Determine children: first level uses categories, second level uses applications
        let children = item.categories || item.applications
        // If children are strings (leaf nodes), convert them into objects
        if (children && typeof children[0] === "string") {
          children = children.map((name, i) => ({
            name,
            id: `${id}.${i + 1}`,
            children: undefined
          }))
        }
        return {
          ...item,
          id, // hierarchical ID
          name: item.name,
          children:
            children && children.length
              ? this.addIdsAndNormalizeSegments(children, id)
              : undefined // undefined = treated as leaf by TreeSelect and by Vuetify's own internal group tracking
        }
      })
    },
    convertSelectedSegmentsfunction(filteredValues, source) {
      const result = []

      for (const path of filteredValues) {
        const parts = path.split(".").map(Number)

        const segmentId = parts[0]
        const categoryId = parts[1]
        const applicationIndex = parts[2]

        // CASE 1: only ["5"]
        if (parts.length === 1) {
          result.push({ segmentId: segmentId })
          continue
        }

        const segment = source.find(x => x.id == segmentId)
        if (!segment) {
          continue
        }

        const categories = segment.categories?.find(x => x.id == categoryId)
        if (!categories) {
          continue
        }

        let output = {
          segmentId: segmentId,
          categoryId: categoryId
        }

        // CASE 3: ["2.1.3"]
        if (parts.length === 3) {
          const value = categories.applications?.[applicationIndex - 1]
          if (value === undefined) {
            continue
          }
          output.applications = value
        }

        // CASE 2: ["2.1"] → do nothing extra
        result.push(output)
      }
      return result
    },
    // addIdsAndNormalizeProducts(data) {
    //   return data.map(this.normalizeNode)
    // },

    // normalizeNode(node) {
    //   return {
    //     id: node.productId,
    //     name: node.productName,
    //     children: (node.items || []).map(this.normalizeNode)
    //   }
    // },
    addIdsAndNormalizeProducts(items) {
      //Mapping products to have id, name and children.
      return items.map(group => ({
        id: group.productId,
        name: group.productName,
        children: group.items.map(item => ({
          id: item.productId,
          name: item.productName
        }))
      }))
    },
    formatDate(date) {
      return Formatter().formatDate(date)
    },
    formatEngineHours(hours) {
      if (hours == undefined || hours == 0) {
        return "-"
      }
      return hours + " h"
    },
    machineImageSrc(item) {
      if (item.isSpp) {
        return `/products/jpg/${item.productId}_SPP.jpg`
      }
      return `/products/jpg/${item.productId}.jpg`
    },
    // Validation: only check if string is non-empty or array has items
    isValidValue(value) {
      if (value === null || value === undefined) {
        return false
      }

      if (typeof value === "string") {
        return value !== "" // non-empty string
      }

      if (Array.isArray(value)) {
        return value.length > 0 // array has at least one item
      }

      return false // anything else is invalid
    },
    // Remove filter attributes without value
    cleanFilterData() {
      const cleanedFilterData = {}
      // Loop through each field in form
      for (const key in this.allFilters) {
        const value = this.allFilters[key]
        // Only include valid fields
        if (this.isValidValue(value)) {
          cleanedFilterData[key] = value
        }
      }
      return cleanedFilterData
    },
    async onApplyFilters() {
      this.cleanFilterData() // Remove empty filtervalues
      await this.fetchData()
    },
    onSearch() {
      if (this.$refs.freehandFilter.validate()) {
        this.allFilters.SearchString = this.search
      }
    },
    formatIsoDate(date) {
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${date.getFullYear()}-${month}-${day}`
    },
    onTimeRange(range) {
      if (range && range != "custom") {
        this.allFilters.Start = range
      }
    },
    exportToCsv() {
      // Make application a string instead
      let machinesAsString = this.dataTableItems.map(d => ({
        ...d,
        applications: Array.isArray(d.applications)
          ? `"${d.applications.join(", ")}"`
          : d.applications && d.applications.length
          ? d.applications
          : ""
      }))
      // Make a copy of the header items, and remove the first one (image)
      let csvHeaderItems = [...this.headers]
      csvHeaderItems.splice(0, 1)

      const rows = Exporter().exportFleetAnalyticsTable(
        csvHeaderItems,
        machinesAsString
      )
      this.snackbarText = this.$t("common.messages.recordsExported", {
        number: rows
      })
      this.snackbar = true
    }
  },
  watch: {
    tenantUid: {
      immediate: true, // created already handled first load
      handler() {
        this.allFilters.FilterTenantUid = this.tenantUid
      }
    },
    allFilters: {
      immediate: true, // Instead of at (created())
      handler() {
        this.onApplyFilters()
      },
      deep: true
    },
    selectedTimeRange(newRange) {
      // Display date picker. OK callback will trigger fetch
      if (newRange == "custom") {
        this.datePickerMenu = true
      }
    },
    dates(newDates) {
      // A range selection holds every date between start and end (sorted)
      if (!newDates || newDates.length < 2) {
        this.dateRangeText = ""
        return
      }
      const start = this.formatIsoDate(newDates[0])
      const end = this.formatIsoDate(newDates[newDates.length - 1])
      this.dateRangeText = `${start} ~ ${end}`

      this.allFilters.Start = start
      this.allFilters.End = end
      this.onApplyFilters()
    }
  }
}
</script>
<style>
/* To stop cursor/pointer from changing on table hover*/
.no-pointer-change .v-table__wrapper tr {
  cursor: default !important;
}
</style>
